import { useState, useEffect, useRef } from 'react';
import {
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Legend,
  LineChart,
  Line
} from 'recharts';
import { FaTrash, FaBroom, FaMoneyBillWave } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import { auth, db } from '../firebase';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { logActivity, incrementStat } from '../lib/activityLogger';



// Custom Modal Component for confirmation
const ConfirmModal = ({ isOpen, onConfirm, onCancel, message }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl w-80 max-w-full transform transition-all">
        <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">{message}</h3>
        <div className="flex justify-end space-x-4">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

const ExpenseTracker = () => {
  const { theme } = useTheme();
  const [expenses, setExpenses] = useState([]);
  const [item, setItem] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');
  const [category, setCategory] = useState('Travel');
  const [label, setLabel] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isLocalUpdate = useRef(false); // prevents save loop from remote updates

  // Load expenses from Firestore on mount (real-time listener)
  useEffect(() => {
    const unsubAuth = auth.onAuthStateChanged((user) => {
      if (!user) return;
      const expDocRef = doc(db, "users", user.uid, "data", "expenses");
      const unsubSnap = onSnapshot(expDocRef, (snap) => {
        if (snap.exists() && !isLocalUpdate.current) {
          const data = snap.data();
          if (data.items) setExpenses(data.items);
        }
        isLocalUpdate.current = false;
      });
      return () => unsubSnap();
    });
    return () => unsubAuth();
  }, []);

  // Save expenses to Firestore whenever they change (debounced)
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    const timeout = setTimeout(() => {
      isLocalUpdate.current = true;
      const expDocRef = doc(db, "users", user.uid, "data", "expenses");
      setDoc(expDocRef, { items: expenses, totalSpent: expenses.reduce((a, c) => a + c.amount, 0) }, { merge: true })
        .catch((err) => console.warn("Failed to save expenses:", err));
    }, 500);
    return () => clearTimeout(timeout);
  }, [expenses]);

  const handleAddExpense = () => {
    if (item && amount && date && category) {
      const newExpense = {
        item,
        amount: parseFloat(amount),
        date,
        category,
        label: label || category,
      };
      setExpenses([...expenses, newExpense]);

      // Log activity & update stats in Firestore
      logActivity("expense_added", `Added expense: ${item} — ₹${parseFloat(amount).toFixed(2)}`);
      incrementStat("totalExpenses", parseFloat(amount));

      setItem('');
      setAmount('');
      setDate('');
      setCategory('Travel');
      setLabel('');
    }
  };

  const handleDelete = (index) => {
    const removed = expenses[index];
    setExpenses(expenses.filter((_, i) => i !== index));
    // Decrement total expenses stat
    if (removed) incrementStat("totalExpenses", -removed.amount);
  };

  const handleClearAll = () => {
    // Decrement total expenses stat by current total
    const total = expenses.reduce((a, c) => a + c.amount, 0);
    if (total > 0) incrementStat("totalExpenses", -total);
    setExpenses([]);
    setIsModalOpen(false);
  };
  
  // Custom confirmation logic
  const confirmClearAll = () => {
    setIsModalOpen(true);
  };
  
  const totalSpent = expenses.reduce((acc, curr) => acc + curr.amount, 0);

  useEffect(() => {
      window.scrollTo(0, 0);
    }, []);
  // Advanced analytics data
  const monthlyData = [];
  const categoryData = [];
  const forecastData = [];
  if (expenses.length > 0) {
    // Monthly trends
    const monthMap = {};
    expenses.forEach(exp => {
      const month = exp.date.slice(0,7); // YYYY-MM
      monthMap[month] = (monthMap[month] || 0) + exp.amount;
    });
    for (const month in monthMap) {
      monthlyData.push({ month, amount: monthMap[month] });
    }
    // Category breakdown
    const catMap = {};
    expenses.forEach(exp => {
      catMap[exp.category] = (catMap[exp.category] || 0) + exp.amount;
    });
    for (const cat in catMap) {
      categoryData.push({ category: cat, amount: catMap[cat] });
    }
    // Simple forecast: average monthly spend projected for next 3 months
    const months = Object.keys(monthMap);
    const avg = months.length > 0 ? monthlyData.reduce((a,b) => a+b.amount,0)/months.length : 0;
    for (let i=1; i<=3; i++) {
      forecastData.push({ month: `Forecast ${i}`, amount: avg });
    }
  }
  return (
    <div className={`min-h-screen p-4 transition-colors duration-300 ${theme === 'dark' ? 'bg-gray-900' : 'bg-gradient-to-br from-purple-100 to-blue-100'}`}>
      <ConfirmModal
        isOpen={isModalOpen}
        onConfirm={handleClearAll}
        onCancel={() => setIsModalOpen(false)}
        message="Are you sure you want to clear all expenses?"
      />
      <div className="max-w-3xl mx-auto rounded-2xl shadow-xl p-8 bg-white dark:bg-gray-800 transition-colors duration-300 mt-16">
        <h1 className="text-3xl font-extrabold flex items-center justify-center gap-3 text-center mb-6 text-gray-900 dark:text-white">
          <FaMoneyBillWave className="text-green-600 dark:text-green-400 text-4xl" />
          Expense Tracker
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            type="text"
            placeholder="Expense item"
            className="p-3 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
            value={item}
            onChange={(e) => setItem(e.target.value)}
          />
          <input
            type="number"
            placeholder="Amount (₹)"
            className="p-3 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            min="0"
          />
          <input
            type="date"
            className="p-3 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
            value={date}
            max={new Date().toISOString().split('T')[0]} 
            onChange={(e) => setDate(e.target.value)}
          />
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="p-3 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
          >
            <option value="Travel">Travel</option>
            <option value="Food">Food</option>
            <option value="Shopping">Shopping</option>
            <option value="Lodging">Lodging</option>
            <option value="Other">Other</option>
          </select>
          <input
            type="text"
            placeholder="Label (optional)"
            className="p-3 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <button
            onClick={handleAddExpense}
            className="bg-green-500 hover:bg-green-600 text-white p-3 rounded-lg font-semibold transition"
          >
            + Add
          </button>
        </div>

        <h2 className="text-xl font-semibold mt-8 text-center text-gray-800 dark:text-gray-200">
          Total Spent: ₹{totalSpent.toFixed(2)}
        </h2>
        {/* Advanced Analytics Section */}
        {expenses.length > 0 && (
          <div className="mt-10 grid gap-8 grid-cols-1 md:grid-cols-3">
            <div>
              <h3 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">Monthly Trends</h3>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={monthlyData}>
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="amount" stroke="#8884d8" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">Category Breakdown</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={categoryData}>
                  <XAxis dataKey="category" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="amount" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">Spending Forecast</h3>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={forecastData}>
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="amount" stroke="#ff7300" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}


        <div className="mt-10">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200">Expense List</h3>
            {expenses.length > 0 && (
              <button
                onClick={confirmClearAll}
                className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-md text-sm transition"
              >
                <FaBroom /> Clear All
              </button>
            )}
          </div>

          <ul className="space-y-2">
            {expenses.map((exp, idx) => (
              <li
                key={idx}
                className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg shadow-sm hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200"
              >
                <div>
                  <p className="font-medium text-gray-800 dark:text-white">
                    {exp.item} ({exp.label})
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{exp.date}</p>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-semibold text-green-600 dark:text-green-400">
                    ₹{exp.amount}
                  </span>
                  <button
                    onClick={() => handleDelete(idx)}
                    className="p-2 text-red-500 hover:text-red-700 dark:hover:text-red-400 transition-colors duration-200"
                    title="Delete"
                  >
                    <FaTrash />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ExpenseTracker;